import {
  faTemperatureEmpty,
  faTemperatureFull,
  faTemperatureHalf,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Pagination } from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  sxPagination,
  sxPaginationContrast,
} from "../../../assets/styles/CommonSxProps";
import DefaultButton from "../../../components/Accessibility/DefaultButton";
import DefaultText from "../../../components/Accessibility/DefaultText";
import DefaultTextBold from "../../../components/Accessibility/DefaultTextBold";
import HighlightedText from "../../../components/Accessibility/HighlightedText";
import PageTitle from "../../../components/Accessibility/PageTitle";
import VoltarButton from "../../../components/Accessibility/VoltarButton";
import WhiteContainer from "../../../components/Accessibility/WhiteContainer";
import LoadingApp from "../../../components/LoadingApp";
import ModalFeedBack from "../../../components/ModalFeedBack";
import { HistoricoItem } from "../../../interfaces/entities/SaudeBemEstar/AutoAvaliacao/HistoricoItem";
import { Texto } from "../../../interfaces/entities/Texto";
import {
  getHistoricoDataAvaliacao,
  getRefazerAvaliacao,
} from "../../../services/SaudeBemEstar/SaudeEmocionalService";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import { scrollTo } from "../../../utils/ScrollUtil";
import AutoAvaliacaoHistorico from "./AutoAvaliacaoHistorico";
import "./styles.css";
import {
  sxInfoTempContainer,
  sxInfoTempRow,
  sxInfoTextContainer,
  sxTempContainer,
} from "./sxProps";

const AutoAvaliacao: React.FC = () => {
  const refazer = !!useParams().refazer;
  const navigate = useNavigate();
  const accessibility = useAccessibility();
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const rowsPerPage = 5;

  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: "error" | "success" | "warning";
    title?: string;
    textButton?: string;
    subTitle?: string;
    closeAction: () => void;
    showButtonModal: boolean;
    buttonAction?: () => void;
  }>(null);

  const openModal = (
    icon: "error" | "success" | "warning",
    title: string,
    subtitle: string,
    textButton: string,
    closeAction: () => void,
    buttonAction: () => void
  ) => {
    setModalFeedback({
      icon: icon,
      title: title,
      subTitle: subtitle,
      textButton: textButton,
      showButtonModal: true,
      closeAction: closeAction,
      buttonAction: buttonAction,
    });
  };

  const paginatedHistory = historico.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  useEffect(() => {
    getHistoricoDataAvaliacao()
      .then((response) => {
        setHistorico(response);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    scrollTo("scroll-ref");
  };

  const handleClickIniciarAutoAvaliacao = async () => {
    if (refazer) {
      setLoading(true);
      const response = await getRefazerAvaliacao();
      if (response?.status === 200) {
        const lstTextos: Texto[] = response.data.lstTextos;
        openModal(
          "warning",
          "Espere um pouco para refazer a avaliação",
          `${lstTextos[0].texto} <br><br> ${lstTextos[1].texto}`,
          "Fechar",
          () => navigate("/saudeBemEstar/unimedAcolhe"),
          () => navigate("/saudeBemEstar/unimedAcolhe")
        );
      } else {
        openModal(
          "error",
          "Erro ao consultar disponibilidade",
          `Não foi possível validar a disponibilidade de refazer sua auto-avaliação. Tente novamente em instantes`,
          "Fechar",
          () => setModalFeedback(null),
          () => setModalFeedback(null)
        );
      }
      setLoading(false);
    } else {
      navigate("/saudeBemEstar/unimedAcolhe/autoAvaliacao/info");
    }
  };

  return (
    <>
      <LoadingApp showLoading={loading} text={"Carregando..."} />
      <div className="page-container">
        <PageTitle text="Avaliação" />

        <Box sx={sxInfoTextContainer}>
          <DefaultText text="A autoavaliação ajuda a refletir em suas emoções e ajuda a acompanhar sua evolução e auto-entendimento." />
          <DefaultText text="Vamos lá?" />
        </Box>

        <DefaultButton
          onClick={handleClickIniciarAutoAvaliacao}
          text="Iniciar Avaliação"
        />

        <DefaultTextBold component="h2" text="Saiba mais sobre os tipos de resultado" />
        <Box sx={sxInfoTempContainer}>
          <Box sx={sxInfoTempRow}>
            <WhiteContainer>
              <button
                className="saiba-mais-button"
                aria-label="o que é o status 'Baixo'"
                onClick={() =>
                  navigate(
                    "/saudeBemEstar/unimedAcolhe/autoAvaliacao/resultado/B"
                  )}
                >
                <FontAwesomeIcon
                  icon={faTemperatureEmpty}
                  className="temperature-icon"
                  color="#156D44"
                />
                <HighlightedText text='"Baixo"' />
              </button>
            </WhiteContainer>

            <WhiteContainer>
              <button
                className="saiba-mais-button"
                aria-label="o que é o status 'Moderado'"
                onClick={() =>
                  navigate(
                    "/saudeBemEstar/unimedAcolhe/autoAvaliacao/resultado/M"
                  )}
                >
                <FontAwesomeIcon
                  icon={faTemperatureHalf}
                  className="temperature-icon"
                  color="#F47920"
                />
                <HighlightedText text='"Moderado"' />
              </button>
            </WhiteContainer>

            <WhiteContainer>
                <button
                  className="saiba-mais-button"
                  aria-label="o que é o status 'Elevado'"
                  onClick={() =>
                    navigate(
                      "/saudeBemEstar/unimedAcolhe/autoAvaliacao/resultado/A"
                    )}
                  >
                  <FontAwesomeIcon
                    icon={faTemperatureFull}
                    className="temperature-icon"
                    color="#D84141"
                  />
                  <HighlightedText text='"Elevado"' />
                </button>              
              </WhiteContainer>
          </Box>      
        </Box>
        <DefaultTextBold component="h2" text="Histórico" id="scroll-ref" />
        {historico.length > 0 && (
          <>
            <AutoAvaliacaoHistorico dados={paginatedHistory} />
            {historico.length > rowsPerPage && (
              <Pagination
                className="pagination-holder"
                count={Math.ceil(historico.length / rowsPerPage)}
                sx={
                  accessibility.contrastMode
                    ? sxPaginationContrast
                    : sxPagination
                }
                hideNextButton
                hidePrevButton
                color="primary"
                page={page}
                onChange={handlePageChange}
              />
            )}
          </>
        )}

        <VoltarButton
          description="Voltar para unimed acolhe"
          onClick={() => {
            navigate("/saudeBemEstar/unimedAcolhe");
          }}
        />
      </div>
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as "success" | "error" | "warning"}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
        classNameModal="modal-feedback-autoavaliacao"
      />
    </>
  );
};

export default AutoAvaliacao;
